import { rem } from 'polished'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type { ExploreItemMapMarker } from '../utils'

import * as Analytics from 'analytics/analytics'
import NestableClickable from 'components/Common/NestableClickable'
import IconButton from 'LuxKit/Button/IconButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import SolidTimesIcon from 'LuxKit/Icons/solid/SolidTimesIcon'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ModalContext from 'contexts/ModalContext'
import { preventDefault } from 'lib/web/eventHandlerUtils'
import ExploreSummaryCard from 'tripPlanner/components/TripMaps/SummaryCards/Explore/ExploreSummaryCard'
import ExploreItemDetails from 'tripPlanner/components/TripModal/ExploreItems/ExploreItemDetails'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import type { ExploreItem } from 'tripPlanner/types/common'
import useDetectCarouselSlide from 'hooks/useDetectCarouselSlide'
import { exploreItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(-56)};
  right: ${rem(16)};
  z-index: 9999;
  box-shadow: ${(props) => props.theme.shadow.flat.large};
`

const cardWidth = `min(${rem(300)}, 80vw)`

const InnerContainer = styled.div`
  width: ${cardWidth};
  margin-inline: auto;
`

const FullHeightExploreSummaryCard = styled(ExploreSummaryCard)`
  height: 100%;
`

interface Props {
  exploreItems: Array<ExploreItem>
  selectedMarker: ExploreItemMapMarker
}

function ExploreCardCarousel({
  exploreItems,
  selectedMarker,
}: Props) {
  const intl = useIntl()
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const cardElements = useRef<{ [id: string]: HTMLElement }>({})
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const onCardSelect = useCallback(
    (id: string) => {
      onSelectMapItem(exploreItemSelectionId(id))
    },
    [onSelectMapItem],
  )

  // Scroll to correct card when pin selection changes
  useEffect(() => {
    if (
      carouselRef.current &&
      cardElements.current[selectedMarker.item.id]
    ) {
      // Determine distance to scroll to get the relevant card into view
      const cardRect =
        cardElements.current[selectedMarker.item.id].getBoundingClientRect()
      const currentCentre = cardRect.left + cardRect.width / 2
      const targetCentre = window.innerWidth / 2
      const scrollBy = currentCentre - targetCentre

      // Determine whether the carousel is already visible
      const carouselRect = carouselRef.current.getBoundingClientRect()
      const midPoint = (window.innerHeight + carouselRect.height) / 2
      const isAlreadyUp = carouselRect.top < midPoint

      // Rounding errors caused by screen scaling can cause scrollBy to be something weird
      // like 0.2. This causes the carousel to advance to the next card, which is not what we
      // want. Checking if scrollBy is at least 1 avoids this.
      if (Math.abs(scrollBy) >= 1) {
        carouselRef.current.scrollBy({
          left: scrollBy,
          behavior: isAlreadyUp ? 'smooth' : 'instant',
        })
      }
    }
  })

  const onScroll = useDetectCarouselSlide(
    carouselRef,
    cardElements,
    onCardSelect,
  )

  const tripId = useTripId()
  const showModal = useContext(ModalContext)

  const openDetailsModal = useCallback(
    (item: ExploreItem) => {
      showModal(<ExploreItemDetails item={item} tripId={tripId} />)

      Analytics.trackClientEvent({
        action: 'click',
        subject: 'view_more_details_explore_item',
        category: AnalyticsPage.tripPlanner,
        type: 'interaction',
      })
    },
    [tripId, showModal],
  )

  return (
    <InnerContainer onScroll={preventDefault}>
      <CloseButton
        kind="primary"
        variant="ghost"
        size="medium"
        shape="circle"
        aria-label={intl.formatMessage({
          id: 'SEIzmNz8RN_fgEt71VUp',
          defaultMessage: 'Close',
          description: 'Aria-label for the close button on the explore card carousel map overlay',
        })}
        onClick={onDeselectMapItem}
      >
        <SolidTimesIcon />
      </CloseButton>
      <CardCarousel
        ref={carouselRef}
        snap="center"
        width={cardWidth}
        gutterSize={`calc(100vw - min(${cardWidth}, 100vw))`}
        gap={12}
        onScroll={onScroll}
      >
        {exploreItems.map((item) => (
          <NestableClickable
            key={item.id}
            ref={(element) => {
              if (element) cardElements.current[item.id] = element
            }}
            onClick={() => {
              openDetailsModal(item)
            }}
          >
            <FullHeightExploreSummaryCard item={item} />
          </NestableClickable>
        ))}
      </CardCarousel>
    </InnerContainer>
  )
}

export default ExploreCardCarousel
